import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../services/api';
import { ADMIN_PATH_FEATURE } from '../components/Sidebar';
import { hasFeature } from '../utils/featureFlags';
import { Award } from 'lucide-react';
import { MapContainer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import BaseTileLayer from '../components/BaseTileLayer';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  Users, CheckCircle2, Ship, AlertTriangle,
  MapPin, Activity, BarChart2, Clock,
  RefreshCw, ArrowRight, Circle,
  ClipboardList, CalendarDays, FileText, Bell, Settings,
} from 'lucide-react';

interface AdminUser { id: string; name: string; role: string; }
interface Props { user: AdminUser; }

interface VesselGps {
  id: string; vessel_name: string; vessel_code: string;
  status: string; color: string; lat: number; lng: number; trail: [number, number][];
  positionUnknown?: boolean;
}
interface ActivityItem { time: string; icon: string; color: string; text: string; sub: string; }
interface DashStats { totalCrew: number; workingNow: number; vessels: number; alerts: number; }
interface WeeklyPoint { date: string; label: string; count: number; }
interface OtRank { user_name: string; total_overtime_minutes: number; }
interface OnboardCrew { user_name: string; user_role: string; vessel_name: string; session_status: string; }
interface DashDetail { weekly: WeeklyPoint[]; overtimeRanking: OtRank[]; onboard: OnboardCrew[]; }

// 運航状態は打刻から自動判定される（荷役→入港中 / 航海・当直→航行中 / 勤務者なし→停泊中）
const STATUS_COLOR: Record<string, string> = {
  sailing: '#0EA5E9', anchored: '#94A3B8', offline: '#64748B', port: '#22C55E',
};
const STATUS_LABEL: Record<string, string> = {
  sailing: '航行中', anchored: '停泊中', offline: 'オフライン', port: '入港中',
};

function createDotIcon(color: string) {
  return L.divIcon({
    html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.25)"></div>`,
    className: '', iconSize: [12, 12], iconAnchor: [6, 6],
  });
}

/* カードヘッダー共通 */
function SectionHeader({ icon: Icon, title, badge, linkTo, onNavigate }: {
  icon: React.ElementType; title: string; badge?: React.ReactNode;
  linkTo?: string; onNavigate?: (path: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: '#EBF4FF', flexShrink: 0 }}>
        <Icon size={14} strokeWidth={2.2} color="#0066CC" />
      </span>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827', flex: 1 }}>{title}</h2>
      {badge}
      {linkTo && onNavigate && (
        <button
          onClick={() => onNavigate(linkTo)}
          style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#0066CC', padding: '2px 0', flexShrink: 0 }}
        >
          すべて見る <ArrowRight size={12} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

/* ステータスバッジ */
function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLOR[status] ?? '#94A3B8';
  const label = STATUS_LABEL[status] ?? status;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: color + '18', color,
    }}>
      <Circle size={6} fill={color} strokeWidth={0} />
      {label}
    </span>
  );
}

/* カードラッパー */
function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: '1px solid #E5E7EB',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      padding: '20px 22px',
      ...style,
    }}>
      {children}
    </div>
  );
}

const DashboardPage: React.FC<Props> = ({ user }) => {
  const navigate = useNavigate();
  const [vessels,     setVessels]     = useState<VesselGps[]>([]);
  const [activities,  setActivities]  = useState<ActivityItem[]>([]);
  const [stats,       setStats]       = useState<DashStats>({ totalCrew: 0, workingNow: 0, vessels: 0, alerts: 0 });
  const [detail,      setDetail]      = useState<DashDetail>({ weekly: [], overtimeRanking: [], onboard: [] });
  const [loading,     setLoading]     = useState(true);
  // 要対応: 役職未登録の乗船者と、直近の自己乗下船（船員アプリからの登録）
  const [todoMissing, setTodoMissing] = useState<Array<{ assignment_id: string; user_name: string; vessel_name: string; boarded_at: string | null }>>([]);
  const [todoSelf, setTodoSelf] = useState<Array<{ id: string; record_type: string; recorded_at: string; user_name: string; vessel_name: string; current_position: string | null }>>([]);
  useEffect(() => {
    get<any>('/vessels/assignments/missing-position').then(r => setTodoMissing(r?.data ?? [])).catch(() => {});
    get<any>('/vessels/assignments/recent-self-boarding', { days: '7' }).then(r => setTodoSelf(r?.data ?? [])).catch(() => {});
  }, []);
  const [selected,    setSelected]    = useState<VesselGps | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // 役職ベースの制限付き管理者（船長など）: アクセスできる項目だけ表示する
  const restricted = !['system_admin', 'shore_admin'].includes(user.role ?? '');
  const [allowedFeat, setAllowedFeat] = useState<Record<string, boolean> | null>(restricted ? null : {});
  useEffect(() => {
    if (!restricted) { setAllowedFeat({}); return; }
    get<any>('/position-permissions/my').then(r => setAllowedFeat(r?.data ?? {})).catch(() => setAllowedFeat({}));
  }, [restricted]);
  /** そのページ(パス)を開ける権限があるか。制限なしユーザー・参照系ページは常に true。 */
  const can = (path?: string): boolean => {
    if (!restricted) return true;
    if (!path) return false;
    const key = Object.keys(ADMIN_PATH_FEATURE).find(k => path === k || path.startsWith(k + '/'));
    const feat = key ? ADMIN_PATH_FEATURE[key] : undefined;
    if (!feat) return true;                 // /dashboard 等の参照系
    if (feat === '_adminOnly') return false; // 設定・マスタ等は管理者専用
    return !!(allowedFeat && allowedFeat[feat]);
  };

  useEffect(() => {
    const load = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [vesRes, actRes, statRes, detRes] = await Promise.allSettled([
          get<any>('/vessels/gps'),
          get<any>('/activity'),
          get<any>('/dashboard/stats'),
          get<any>('/dashboard/detail'),
        ]);

        if (vesRes.status === 'fulfilled' && vesRes.value) {
          const raw = vesRes.value?.vessels ?? vesRes.value?.data ?? [];
          setVessels(raw.map((v: any) => ({
            id: String(v.id ?? ''), vessel_name: v.vessel_name ?? '',
            vessel_code: v.vessel_code ?? '', status: v.status ?? 'offline',
            color: STATUS_COLOR[v.status ?? 'offline'] ?? '#94A3B8',
            positionUnknown: Boolean(v.position_unknown),
            lat: v.position_unknown ? NaN : Number(v.lat ?? v.latitude ?? 35.0),
            lng: v.position_unknown ? NaN : Number(v.lng ?? v.longitude ?? 136.0),
            trail: Array.isArray(v.trail) ? v.trail : [],
          })));
        }
        if (actRes.status === 'fulfilled' && actRes.value) {
          const raw = actRes.value?.activities ?? actRes.value?.data ?? [];
          setActivities(raw.slice(0, 8).map((a: any) => ({
            time: String(a.time ?? a.created_at ?? '').slice(11, 16) || '',
            icon: a.icon ?? '', color: a.color ?? '#0066CC',
            text: a.text ?? a.message ?? '', sub: a.sub ?? a.vessel_name ?? '',
          })));
        }
        if (statRes.status === 'fulfilled' && statRes.value) {
          const s = statRes.value;
          setStats({
            totalCrew:  Number(s.total_crew  ?? s.totalCrew  ?? 0),
            workingNow: Number(s.working_now ?? s.workingNow ?? 0),
            vessels:    Number(s.vessels     ?? 0),
            alerts:     Number(s.alerts      ?? 0),
          });
        }
        if (detRes.status === 'fulfilled' && detRes.value) {
          const d = detRes.value;
          setDetail({
            weekly:          Array.isArray(d.weekly)          ? d.weekly          : [],
            overtimeRanking: Array.isArray(d.overtimeRanking) ? d.overtimeRanking : [],
            onboard:         Array.isArray(d.onboard)         ? d.onboard         : [],
          });
        }
      } catch (err) { console.error('[DashboardPage]', err); }
      if (!silent) setLoading(false);
      setLastUpdated(new Date());
    };
    load();
    const timer = setInterval(() => load(true), 60_000);
    return () => clearInterval(timer);
  }, []);

  // 資格・期限管理（機能フラグの会社のみ）: 期限切れ・警告の件数をカードに出す
  const [certSummary, setCertSummary] = useState<{ expired: number; danger: number } | null>(null);
  useEffect(() => {
    if (!hasFeature('crew_certificates') || !can('/crew-certificates')) return;
    const run = () => get<any>('/crew-certificates/summary').then(r => { if (r?.data) setCertSummary({ expired: Number(r.data.expired ?? 0), danger: Number(r.data.danger ?? 0) }); }).catch(() => { /* 権限なし等は出さない */ });
    run();
    const t = setInterval(run, 5 * 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* KPIカード定義 */
  const KPI_CARDS = [
    { label: '総乗組員数',   value: stats.totalCrew,  Icon: Users,         color: '#0066CC', bg: '#EBF4FF', path: '/users' },
    // 資格・期限管理（機能フラグの会社のみ）: 期限切れ＋警告の件数
    ...(certSummary ? [{ label: '期限が近い資格', value: certSummary.expired + certSummary.danger, Icon: Award, color: '#7C3AED', bg: '#F3E8FF', path: '/crew-certificates' }] : []),
    { label: '現在勤務中',   value: stats.workingNow, Icon: CheckCircle2,  color: '#16A34A', bg: '#DCFCE7', path: '/attendance' },
    { label: '船舶稼働数',   value: stats.vessels,    Icon: Ship,          color: '#6366F1', bg: '#EDE9FE', path: '/vessels' },
    { label: 'アラート件数', value: stats.alerts,     Icon: AlertTriangle, color: '#DC2626', bg: '#FEE2E2', path: '/alerts' },
  ];

  return (
    <div style={{ padding: '28px 36px', background: '#F4F6F8', minHeight: '100vh' }}>

      {/* ページヘッダー */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>
            ダッシュボード
          </h1>
          <p style={{ margin: '3px 0 0', color: '#6B7280', fontSize: 13 }}>
            ようこそ、{user.name} さん
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdated && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#9CA3AF' }}>
              <RefreshCw size={12} strokeWidth={2} />
              {lastUpdated.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })} 更新
            </span>
          )}
          {can('/attendance') && (
          <button
            onClick={() => navigate('/attendance')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#0066CC', color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            勤怠管理へ <ArrowRight size={14} strokeWidth={2.2} />
          </button>
          )}
        </div>
      </div>

      {/* KPIカード — クリックで各ページへ（アクセス可能なものだけ） */}
      {(() => {
        const kpiCards = KPI_CARDS.filter(c => can(c.path));
        if (kpiCards.length === 0) return null;
        return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${kpiCards.length},1fr)`, gap: 16, marginBottom: 20 }}>
        {kpiCards.map(c => (
          <div
            key={c.label}
            onClick={() => navigate(c.path)}
            style={{
              background: '#fff', borderRadius: 12, padding: '18px 20px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              display: 'flex', alignItems: 'center', gap: 16,
              cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)';
              e.currentTarget.style.borderColor = c.color + '66';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={{
              width: 44, height: 44, borderRadius: 11, background: c.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <c.Icon size={20} strokeWidth={2} color={c.color} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
                {loading ? <span style={{ fontSize: 20, color: '#D1D5DB' }}>—</span> : c.value}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, fontWeight: 500 }}>{c.label}</div>
            </div>
            <ArrowRight size={14} strokeWidth={2} color={c.color} style={{ opacity: 0.5, flexShrink: 0 }} />
          </div>
        ))}
      </div>
        );
      })()}

      {/* 要対応（陸上が確認すべき未処理）。通知は流れて消えるので、状態として残す */}
      {(todoMissing.length > 0 || todoSelf.length > 0) && (
        <div className="card" style={{ marginBottom: 20, border: '1.5px solid #fecaca', background: '#fff7f7' }}>
          <SectionHeader icon={AlertTriangle} title={`要対応（${todoMissing.length + todoSelf.length}件）`} />
          {todoMissing.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#b91c1c', marginBottom: 6 }}>役職が登録されていない乗船者 — 役職が無いと船長の書類作成などの権限が効きません</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {todoMissing.map(m => (
                  <button key={m.assignment_id} onClick={() => navigate('/crew-sim')}
                    style={{ border: '1px solid #fca5a5', background: '#fff', color: '#7f1d1d', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {m.user_name}（{m.vessel_name}{m.boarded_at ? `・${String(m.boarded_at).slice(0, 10)}乗船` : ''}） → 役職を登録
                  </button>
                ))}
              </div>
            </div>
          )}
          {todoSelf.length > 0 && (
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#9a3412', marginBottom: 6 }}>直近7日の自己乗下船（船員アプリからの登録）— 配乗の内容を確認してください</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {todoSelf.map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#431407' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: '1px 8px', borderRadius: 999, background: e.record_type === 'board' ? '#dcfce7' : '#fee2e2', color: e.record_type === 'board' ? '#166534' : '#991b1b' }}>{e.record_type === 'board' ? '乗船' : '下船'}</span>
                    <span style={{ color: '#78716c' }}>{String(e.recorded_at).slice(0, 16).replace('T', ' ')}</span>
                    <span style={{ fontWeight: 700 }}>{e.user_name}</span>
                    <span>{e.vessel_name}</span>
                    {e.record_type === 'board' && !e.current_position && <span style={{ color: '#b91c1c', fontWeight: 700 }}>役職未登録</span>}
                    {e.record_type === 'board' && e.current_position && <span style={{ color: '#64748b' }}>役職: {e.current_position}</span>}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button onClick={() => navigate('/boarding')} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>乗下船記録を見る</button>
                <button onClick={() => navigate('/crew-sim')} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>配乗シミュレーションで役職を登録</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* クイックアクセス */}
      {(() => {
        const QA_ALL = [
          { label: '勤怠一覧', desc: '今日の出退勤を確認',   Icon: ClipboardList, path: '/attendance', color: '#0066CC' },
          { label: 'GPS位置',  desc: '船舶のリアルタイム位置', Icon: MapPin,        path: '/gps',        color: '#0891B2' },
          { label: '月次集計', desc: '今月の勤務サマリー',    Icon: CalendarDays,  path: '/monthly',    color: '#6366F1' },
          { label: 'レポート', desc: 'CSV・レポート出力',      Icon: FileText,      path: '/reports',    color: '#16A34A' },
          { label: 'アラート', desc: '未解決アラートを確認',   Icon: Bell,          path: '/alerts',     color: '#DC2626' },
          { label: '設定',     desc: 'システム・船舶設定',     Icon: Settings,      path: '/settings',   color: '#6B7280' },
        ] as const;
        const QA_ITEMS = QA_ALL.filter(q => can(q.path));
        if (QA_ITEMS.length === 0) return null;
        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
              クイックアクセス
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(QA_ITEMS.length, 6)}, 1fr)`, gap: 10 }}>
              {QA_ITEMS.map(q => (
                <button
                  key={q.path}
                  onClick={() => navigate(q.path)}
                  style={{
                    background: '#fff', borderRadius: 10, padding: '14px 12px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    cursor: 'pointer', textAlign: 'center',
                    transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.09)';
                    e.currentTarget.style.borderColor = q.color + '66';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span style={{
                    width: 36, height: 36, borderRadius: 9,
                    background: q.color + '14',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <q.Icon size={18} strokeWidth={1.8} color={q.color} />
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{q.label}</div>
                    <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1, lineHeight: 1.4 }}>{q.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 地図 + アクティビティ */}
      {(can('/gps') || can('/attendance')) && (
      <div style={{ display: 'grid', gridTemplateColumns: can('/gps') && can('/attendance') ? '3fr 2fr' : '1fr', gap: 16, marginBottom: 16 }}>

        {/* 地図 */}
        {can('/gps') && (
        <Card>
          <SectionHeader icon={MapPin} title="船舶位置" linkTo="/gps" onNavigate={navigate} />
          <div style={{ borderRadius: 8, overflow: 'hidden', height: 320, border: '1px solid #F3F4F6' }}>
            <MapContainer center={[34.5, 136.0]} zoom={6} style={{ height: '100%', width: '100%' }}>
              <BaseTileLayer />
              {vessels.map(v => (
                <React.Fragment key={v.id}>
                  {v.trail.length > 1 && <Polyline positions={v.trail} color={v.color} weight={2} opacity={0.5} />}
                  {!v.positionUnknown && (
                  <Marker position={[v.lat, v.lng]} icon={createDotIcon(v.color)} eventHandlers={{ click: () => setSelected(v) }}>
                    <Popup>
                      <strong>{v.vessel_name}</strong><br />
                      <span style={{ color: '#6B7280', fontSize: 12 }}>{v.vessel_code}</span><br />
                      <StatusBadge status={v.status} />
                    </Popup>
                  </Marker>
                  )}
                </React.Fragment>
              ))}
            </MapContainer>
          </div>
        </Card>
        )}

        {/* アクティビティ */}
        {can('/attendance') && (
        <Card>
          <SectionHeader icon={Activity} title="最近のアクティビティ" linkTo="/attendance" onNavigate={navigate} />
          {loading ? (
            <div style={{ color: '#9CA3AF', fontSize: 13, paddingTop: 8 }}>読み込み中...</div>
          ) : activities.length === 0 ? (
            <div style={{ color: '#9CA3AF', fontSize: 13, paddingTop: 8 }}>データがありません</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activities.map((a, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  padding: '9px 0',
                  borderBottom: i < activities.length - 1 ? '1px solid #F9FAFB' : 'none',
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: a.color,
                    flexShrink: 0, marginTop: 5,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: '#1F2937', fontWeight: 500, lineHeight: 1.4 }}>{a.text}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                      {[a.sub, a.time].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        )}
      </div>
      )}

      {/* グラフ行: 週次出勤 + 残業ランキング（勤怠権限） */}
      {(can('/attendance') || can('/monthly')) && (
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16, marginBottom: 16 }}>

        {/* 週次出勤グラフ */}
        <Card>
          <SectionHeader icon={BarChart2} title="週次出勤人数（過去7日）" linkTo="/monthly" onNavigate={navigate} />
          {loading ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>読み込み中...</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={detail.weekly} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                  formatter={(v: number) => [`${v}名`, '出勤']}
                  labelStyle={{ fontWeight: 700, color: '#111827' }}
                  cursor={{ fill: '#F4F6F8' }}
                />
                <Bar dataKey="count" radius={[5, 5, 0, 0]} maxBarSize={44}>
                  {detail.weekly.map((entry, i) => {
                    const max = Math.max(...detail.weekly.map(w => w.count), 1);
                    return (
                      <Cell key={i}
                        fill={entry.count === 0 ? '#E5E7EB' : entry.count === max ? '#0066CC' : '#BFDBFE'} />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* 残業ランキング */}
        <Card>
          <SectionHeader icon={Clock} title="今月 残業ランキング" linkTo="/monthly" onNavigate={navigate} />
          {loading ? (
            <div style={{ color: '#9CA3AF', fontSize: 13 }}>読み込み中...</div>
          ) : detail.overtimeRanking.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 0', gap: 8 }}>
              <CheckCircle2 size={28} color="#16A34A" strokeWidth={1.8} />
              <span style={{ fontSize: 13, color: '#6B7280' }}>今月は残業なし</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {detail.overtimeRanking.map((r, i) => {
                const h = Math.floor(r.total_overtime_minutes / 60);
                const m = r.total_overtime_minutes % 60;
                const pct = Math.min(100, Math.round(r.total_overtime_minutes / 3600 * 100));
                const color = r.total_overtime_minutes >= 3600 ? '#DC2626'
                  : r.total_overtime_minutes >= 1800 ? '#F97316' : '#F59E0B';
                const rankColors = ['#F59E0B', '#9CA3AF', '#CD7C54'];
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 20, height: 20, borderRadius: 5, fontSize: 11, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: i < 3 ? rankColors[i] + '22' : '#F3F4F6',
                          color: i < 3 ? rankColors[i] : '#6B7280',
                        }}>
                          {i + 1}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>{r.user_name}</span>
                      </div>
                      <span style={{ fontSize: 12, color, fontWeight: 700 }}>
                        {h}h{m > 0 ? `${m}m` : ''}
                      </span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: '#F3F4F6' }}>
                      <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: color, transition: 'width 0.7s ease' }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>バー = 60時間に対する割合</div>
            </div>
          )}
        </Card>
      </div>
      )}

      {/* 乗船中クルー（配乗権限） */}
      {!loading && detail.onboard.length > 0 && can('/crew-assign') && (
        <Card style={{ marginBottom: 16 }}>
          <SectionHeader
            icon={Users}
            title="乗船中クルー"
            linkTo="/crew-assign" onNavigate={navigate}
            badge={
              <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', background: '#F3F4F6', borderRadius: 20, padding: '2px 10px' }}>
                {detail.onboard.length}名
              </span>
            }
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {detail.onboard.map((c, i) => {
              const isWorking = c.session_status === 'working';
              const isResting = c.session_status === 'resting';
              const statusColor = isWorking ? '#16A34A' : isResting ? '#D97706' : '#9CA3AF';
              const statusLabel = isWorking ? '勤務中' : isResting ? '休憩中' : 'オフ';
              return (
                <div key={i} style={{
                  background: '#FAFAFA', borderRadius: 9,
                  padding: '12px 14px',
                  border: '1px solid #F3F4F6',
                  borderLeft: `3px solid ${statusColor}`,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>{c.user_name}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Ship size={10} strokeWidth={1.8} />
                    {c.vessel_name}
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: statusColor, background: statusColor + '18',
                    borderRadius: 20, padding: '2px 9px',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    <Circle size={5} fill={statusColor} strokeWidth={0} />
                    {statusLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* 船舶稼働状況（運航権限） */}
      {can('/vessels') && (
      <Card>
        <SectionHeader icon={Ship} title="船舶稼働状況" linkTo="/vessels" onNavigate={navigate} />
        {loading ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>読み込み中...</div>
        ) : vessels.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>データがありません</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px,1fr))', gap: 10 }}>
            {vessels.map(v => (
              <div key={v.id}
                onClick={() => setSelected(selected?.id === v.id ? null : v)}
                style={{
                  borderRadius: 9, padding: '14px 16px', cursor: 'pointer',
                  border: selected?.id === v.id ? `1.5px solid ${v.color}` : '1px solid #E5E7EB',
                  background: selected?.id === v.id ? v.color + '0A' : '#FAFAFA',
                  transition: 'border-color 0.12s, background 0.12s',
                }}
                onMouseEnter={e => { if (selected?.id !== v.id) (e.currentTarget.style.background = '#F4F6F8'); }}
                onMouseLeave={e => { if (selected?.id !== v.id) (e.currentTarget.style.background = '#FAFAFA'); }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 3 }}>{v.vessel_name}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10 }}>{v.vessel_code}</div>
                <StatusBadge status={v.status} />
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      {/* 制限付きユーザーで表示項目が無い場合の案内 */}
      {restricted && allowedFeat && KPI_CARDS.filter(c => can(c.path)).length === 0 && !can('/gps') && !can('/attendance') && !can('/vessels') && (
        <Card>
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#6B7280' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>ダッシュボードに表示できる項目がありません</div>
            <div style={{ fontSize: 13, lineHeight: 1.7 }}>ご利用できる機能は左メニューからお進みください。<br />表示範囲は管理者が「役職マスタ → 権限設定」で調整できます。</div>
          </div>
        </Card>
      )}

    </div>
  );
};

export default DashboardPage;
